import { useState } from 'react';
import { BackLink } from '../components/layout/BackLink';
import { useRollupsQuery, type RollupEntityKind } from '../api/rollups';
import { ViewToggle, type ViewMode } from '../components/ViewToggle';
import { HistoryChart, type ChartPoint } from '../components/chart/HistoryChart';
import { TrendRangeSelector } from '../components/TrendRangeSelector';
import { TREND_RANGE_DAYS, type TrendRange } from '../components/trendRanges';

type FloorName = 'ground' | 'middle' | 'top';
type GroupName = 'ground' | 'middle_sun' | 'middle_shade' | 'top_sun' | 'top_shade';

const FLOORS: readonly FloorName[] = ['ground', 'middle', 'top'];
const GROUPS: readonly GroupName[] = ['ground', 'middle_sun', 'middle_shade', 'top_sun', 'top_shade'];

const ENTITY_LABEL: Record<string, string> = {
  ground: 'Ground Floor',
  middle: 'Middle Floor',
  top: 'Top Floor',
  middle_sun: 'Middle · Sun',
  middle_shade: 'Middle · Shade',
  top_sun: 'Top · Sun',
  top_shade: 'Top · Shade',
};

function deg(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(1)} °C` : '—';
}
function pct(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v * 100)}%` : '—';
}

// Rollup rows arrive newest-day-first and carry loosely-typed fields (the API
// schema keeps them as `unknown`). Map a named field to chart points, dropping
// rows where it is missing or non-numeric so a partial day reads as a gap
// rather than breaking the scale. `scale` converts stored units to plotted
// ones — fractions are stored 0–1 but charted 0–100.
function rollupPoints(
  rollups: ReadonlyArray<Record<string, unknown>>,
  field: string,
  scale = 1,
): ChartPoint[] {
  return rollups
    .map((r) => {
      const v = r[field];
      const t = Date.parse(String(r['day']));
      return typeof v === 'number' && Number.isFinite(v) && Number.isFinite(t)
        ? { t, v: v * scale }
        : null;
    })
    .filter((p): p is ChartPoint => p !== null);
}

function RollupSection({ kind, name, days }: { kind: RollupEntityKind; name: string; days: number }) {
  const q = useRollupsQuery(kind, name, days);
  const [view, setView] = useState<ViewMode>('Chart');
  const label = ENTITY_LABEL[name] ?? name;
  const rows = q.data ?? [];

  return (
    <div className="card">
      <div className="card-header">
        <strong>{label}</strong>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {q.isFetching && <span className="muted">refreshing…</span>}
          <ViewToggle value={view} onChange={setView} />
        </span>
      </div>
      {q.isLoading && <div className="skeleton" style={{ height: 60 }} />}
      {q.isError && <div className="banner danger">{(q.error as Error).message}</div>}
      {q.data && q.data.length === 0 && <div className="muted">No daily data yet.</div>}
      {q.data && q.data.length > 0 && view === 'Chart' && (
        kind === 'floor' ? (
          <HistoryChart
            width={680}
            height={200}
            series={[{ data: rollupPoints(rows, 'avgReadingC'), label: 'avg' }]}
            areas={[{ lo: rollupPoints(rows, 'minReadingC'), hi: rollupPoints(rows, 'maxReadingC'), colorIndex: 0 }]}
            yLabel="°C"
          />
        ) : (
          <HistoryChart
            width={680}
            height={200}
            series={[{ data: rollupPoints(rows, 'openFraction', 100), label: 'open' }]}
            yLabel="%"
          />
        )
      )}
      {q.data && q.data.length > 0 && view === 'Grid' && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
              <th>Day</th>
              {kind === 'floor' ? (
                <>
                  <th>Avg</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>Calling</th>
                </>
              ) : (
                <th>Open</th>
              )}
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <tr key={String(r.day)}>
                <td>{String(r.day)}</td>
                {kind === 'floor' ? (
                  <>
                    <td className="tabular">{deg(r.avgReadingC)}</td>
                    <td className="tabular">{deg(r.minReadingC)}</td>
                    <td className="tabular">{deg(r.maxReadingC)}</td>
                    <td className="tabular">{pct(r.callFraction)}</td>
                  </>
                ) : (
                  <td className="tabular">{pct(r.openFraction)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Weather rollups carry a different field set than floor/group (avg/min/max
// outside temp, cloud %, peak solar), so they get their own small section
// rather than overloading the shared RollupSection branching.
function WeatherRollupSection({ days }: { days: number }) {
  const q = useRollupsQuery('weather', 'site', days);
  const [view, setView] = useState<ViewMode>('Chart');
  const rows = q.data ?? [];

  return (
    <div className="card">
      <div className="card-header">
        <strong>Weather</strong>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {q.isFetching && <span className="muted">refreshing…</span>}
          <ViewToggle value={view} onChange={setView} />
        </span>
      </div>
      {q.isLoading && <div className="skeleton" style={{ height: 60 }} />}
      {q.isError && <div className="banner danger">{(q.error as Error).message}</div>}
      {q.data && q.data.length === 0 && <div className="muted">No daily data yet.</div>}
      {q.data && q.data.length > 0 && view === 'Chart' && (
        // Solar stays a line against a left W/m² axis. Temperature is bars
        // against a right °C axis, measured from the domain minimum rather
        // than zero (there's no meaningful "0°C floor" for a day's average)
        // with a min/max whisker through each bar. Cloud cover — a third
        // unit again — rides as the temp bar's own colour (a light→dark
        // ramp) rather than claiming a third axis or a separate backdrop.
        // Peak solar stays grid-only — it's a single daily instant, not a
        // trend.
        <HistoryChart
          width={680}
          height={220}
          series={[{ data: rollupPoints(rows, 'avgSolarWm2'), label: 'avg solar' }]}
          bars={[{
            data: rollupPoints(rows, 'avgOutsideTempC'),
            label: 'outside °C',
            axis: 'right',
            fromZero: false,
            colorBy: rollupPoints(rows, 'avgCloudCoverPct', 1 / 100),
            colorByLabel: 'cloud cover',
            whisker: {
              lo: rollupPoints(rows, 'minOutsideTempC'),
              hi: rollupPoints(rows, 'maxOutsideTempC'),
            },
          }]}
          yLabel="W/m²"
          yLabelRight="°C"
        />
      )}
      {q.data && q.data.length > 0 && view === 'Grid' && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
              <th>Day</th>
              <th>Avg</th>
              <th>Min</th>
              <th>Max</th>
              <th>Cloud</th>
              <th>Peak solar</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <tr key={String(r.day)}>
                <td>{String(r.day)}</td>
                <td className="tabular">{deg(r.avgOutsideTempC)}</td>
                <td className="tabular">{deg(r.minOutsideTempC)}</td>
                <td className="tabular">{deg(r.maxOutsideTempC)}</td>
                {/* pct expects a 0–1 fraction; rollup gives 0–100, so divide. */}
                <td className="tabular">
                  {typeof r.avgCloudCoverPct === 'number' ? pct((r.avgCloudCoverPct as number) / 100) : '—'}
                </td>
                <td className="tabular">
                  {typeof r.peakSolarWm2 === 'number' ? `${Math.round(r.peakSolarWm2 as number)} W/m²` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function TrendsPage() {
  const [range, setRange] = useState<TrendRange>('30d');
  const days = TREND_RANGE_DAYS[range];

  return (
    <>
      <BackLink />
      <h1>Trends</h1>
      <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
        Daily rollups — average / min / max floor temperature, the fraction of the day each floor
        was calling or each group was open, and outside weather (temperature, cloud cover, peak
        solar). Kept long-term after the per-cycle history ages out.
      </p>
      <div style={{ marginBottom: 'var(--sp-3)' }}>
        <TrendRangeSelector value={range} onChange={setRange} />
      </div>

      <h2>Floors</h2>
      {FLOORS.map((f) => (
        <RollupSection key={f} kind="floor" name={f} days={days} />
      ))}

      <h2>Groups</h2>
      {GROUPS.map((g) => (
        <RollupSection key={g} kind="group" name={g} days={days} />
      ))}

      <h2>Weather</h2>
      <WeatherRollupSection days={days} />
    </>
  );
}
